/**
 * arbi workspaces                              — list workspaces
 * arbi workspace select/create/delete/update   — workspace CRUD
 * arbi workspace users/add-user/remove-user    — user management
 * arbi workspace set-role/copy                 — role & copy operations
 */

import { Command } from 'commander'
import {
  workspaces,
  projects,
  dm as dmOps,
  formatWorkspaceChoices,
  generateEncryptedWorkspaceKey,
  generateNewWorkspaceKey,
} from '@arbidocs/sdk'
import {
  runAction,
  resolveAuth,
  resolveWorkspace,
  resolveDmCrypto,
  printTable,
  parseJsonArg,
  requireInteractive,
  dryRun,
} from '../helpers.js'
import { resolveRecipient } from './dm.js'
import { store, getConfig, updateConfig, getChatSession, clearChatSession } from '../config.js'
import { promptSelect, promptConfirm } from '../prompts.js'
import { success, error, ref } from '../output.js'
import { updateCompletionCache } from '../completion-cache.js'

/**
 * Resolve a workspace selector (ID or exact/unique case-insensitive name) to a
 * workspace ID. Returns a resolution result so callers can surface helpful
 * errors (not found, ambiguous name match) consistently.
 */
type WorkspaceRow = {
  external_id: string
  name: string
  shared_document_count?: number
  private_document_count?: number
  wrapped_key?: string | null
  users?: { role: string }[]
}

type ResolveResult =
  | { ok: true; id: string; ws: WorkspaceRow }
  | { ok: false; reason: 'not-found' | 'ambiguous'; matches?: WorkspaceRow[] }

function resolveWorkspaceSelector(list: WorkspaceRow[], selector: string): ResolveResult {
  // Exact ID match — fast path and unambiguous.
  const byId = list.find((w) => w.external_id === selector)
  if (byId) return { ok: true, id: byId.external_id, ws: byId }

  // Case-insensitive name match. Must be unique to avoid foot-guns.
  const lower = selector.toLowerCase()
  const byName = list.filter((w) => (w.name ?? '').toLowerCase() === lower)
  if (byName.length === 1) return { ok: true, id: byName[0].external_id, ws: byName[0] }
  if (byName.length > 1) return { ok: false, reason: 'ambiguous', matches: byName }

  return { ok: false, reason: 'not-found' }
}

function printResolveError(list: WorkspaceRow[], selector: string, res: ResolveResult): void {
  if (res.ok) return
  if (res.reason === 'ambiguous') {
    error(`Workspace name "${selector}" is ambiguous — multiple matches:`)
    for (const w of res.matches ?? []) error(`  ${w.external_id}  ${w.name}`)
    error('Use the workspace ID instead.')
    return
  }
  error(`Workspace ${selector} not found.`)
  error('Available workspaces:')
  for (const w of list) error(`  ${w.external_id}  ${w.name}`)
}

export function registerWorkspacesCommand(program: Command): void {
  program
    .command('workspaces')
    .description('List workspaces')
    .option('--json', 'Output as JSON')
    .option('--ids', 'Output only workspace IDs (one per line)')
    .option(
      '--mine',
      'Only workspaces where you have a membership role (hide deployment-wide common ones)'
    )
    .action((opts: { json?: boolean; ids?: boolean; mine?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const allWorkspaces = await workspaces.listWorkspaces(arbi)
        // Cache the full list so completion still sees common workspaces — only
        // the display below is narrowed when `--mine` is set.
        updateCompletionCache(allWorkspaces)
        const data = opts.mine
          ? allWorkspaces.filter((w) => (w.users as { role: string }[])?.length > 0)
          : allWorkspaces

        if (opts.ids) {
          for (const w of data) console.log(w.external_id)
          return
        }

        if (opts.json) {
          const selectedId = getConfig()?.selectedWorkspaceId ?? null
          // Caller's email — we look up *our own* membership row to
          // get *our* role, instead of users[0].role which is whatever
          // membership happens to come first in the response and could
          // belong to a co-worker / another agent in the same workspace.
          const myEmail = store.requireCredentials().email
          const out = data.map((w) => {
            // No row in `users` → backend exposes the workspace because it's
            // deployment-wide ("common"), not because the caller is a member.
            // Surface that explicitly so callers don't read `role: null` as
            // missing data.
            const myRow = (w.users as { user?: { email?: string }; role: string }[])?.find(
              (u) => u?.user?.email === myEmail
            )
            const role = myRow?.role ?? null
            return {
              id: w.external_id,
              name: w.name,
              docs: (w.shared_document_count as number) + (w.private_document_count as number),
              role,
              is_common: role === null,
              is_selected: w.external_id === selectedId,
            }
          })
          console.log(JSON.stringify(out, null, 2))
          return
        }

        if (data.length === 0) {
          process.stderr.write(
            opts.mine
              ? 'No workspaces you are a member of. (Drop --mine to see deployment-wide ones.)\n'
              : 'No workspaces found.\n'
          )
          return
        }

        const selectedId = getConfig()?.selectedWorkspaceId ?? null
        printTable(
          [
            { header: 'ID', width: 24, value: (r) => r.external_id as string },
            {
              header: 'NAME',
              width: 30,
              // Asterisk-mark the active workspace so a glance at the table
              // tells you which one `arbi docs / ask / upload` will hit.
              value: (r) => {
                const name = (r.name as string) ?? ''
                return r.external_id === selectedId ? `* ${name}` : `  ${name}`
              },
            },
            {
              header: 'DOCS',
              width: 6,
              value: (r) =>
                String((r.shared_document_count as number) + (r.private_document_count as number)),
            },
            {
              header: 'ROLE',
              width: 12,
              // `common` (italics dimmed) for memberless rows the caller
              // can see only because it's a deployment-wide workspace.
              // Match by the caller's email so we render *our* role, not
              // whichever membership happens to come first in the array.
              value: (r) => {
                const myRow = (r.users as { user?: { email?: string }; role: string }[])?.find(
                  (u) => u?.user?.email === store.requireCredentials().email
                )
                return myRow?.role ?? 'common'
              },
            },
          ],
          data
        )
      })()
    )

  const workspace = program.command('workspace').description('Workspace management')

  workspace
    .command('current')
    .description('Print the currently selected workspace ID (empty string if none)')
    .option('--json', 'Output full details as JSON')
    .action((opts: { json?: boolean }) =>
      runAction(async () => {
        const selectedId = getConfig()?.selectedWorkspaceId ?? ''
        if (!opts.json) {
          console.log(selectedId)
          return
        }
        if (!selectedId) {
          console.log(JSON.stringify({ id: null, name: null, docs: 0, role: null }, null, 2))
          return
        }
        const { arbi } = await resolveAuth()
        const data = await workspaces.listWorkspaces(arbi)
        const ws = data.find((w) => w.external_id === selectedId)
        if (!ws) {
          // Stale selection — surface it but don't crash.
          console.log(
            JSON.stringify(
              { id: selectedId, name: null, docs: 0, role: null, stale: true },
              null,
              2
            )
          )
          return
        }
        console.log(
          JSON.stringify(
            {
              id: ws.external_id,
              name: ws.name,
              docs: (ws.shared_document_count as number) + (ws.private_document_count as number),
              role: (ws.users as { role: string }[])?.[0]?.role ?? null,
            },
            null,
            2
          )
        )
      })()
    )

  workspace
    .command('select [id-or-name]')
    .description(
      'Select active workspace (accepts ID or unique name; interactive picker if nothing given)'
    )
    .action((idOrName?: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await workspaces.listWorkspaces(arbi)
        updateCompletionCache(data)

        if (data.length === 0) {
          console.log('No workspaces found.')
          return
        }

        let selectedId: string
        if (idOrName) {
          const res = resolveWorkspaceSelector(data as WorkspaceRow[], idOrName)
          if (!res.ok) {
            printResolveError(data as WorkspaceRow[], idOrName, res)
            process.exit(1)
          }
          selectedId = res.id
        } else {
          // Interactive picker
          const choices = formatWorkspaceChoices(data)
          selectedId = await promptSelect('Select workspace', choices)
        }

        updateConfig({ selectedWorkspaceId: selectedId })
        clearChatSession()
        const ws = data.find((w) => w.external_id === selectedId)!
        success(`Selected: ${ws.name} (${ref(selectedId)})`)
      })()
    )

  workspace
    .command('create <name>')
    .description('Create a new workspace')
    .option('-d, --description <text>', 'Workspace description')
    .option('--public', 'Make workspace public', false)
    .option('--select', 'Set the new workspace as the active selection', false)
    .action((name: string, opts: { description?: string; public?: boolean; select?: boolean }) =>
      runAction(async () => {
        const { arbi, loginResult } = await resolveAuth()
        const userProjects = await projects.listProjects(arbi)
        if (!userProjects.length) throw new Error('No projects found. Create a project first.')
        const projectExtId = userProjects[0].external_id
        const encryptedKey = await generateNewWorkspaceKey(arbi, loginResult.serverSessionKey)
        const data = await workspaces.createWorkspace(
          arbi,
          name,
          encryptedKey,
          projectExtId,
          opts.description,
          opts.public ?? false
        )
        if (opts.select) {
          updateConfig({ selectedWorkspaceId: data.external_id })
          clearChatSession()
        }
        // Agents extract the new ID with: grep -oE 'wrk-[a-z0-9]+'
        success(`Created: ${data.name} (${ref(data.external_id)})`)
        if (opts.select) success(`Selected: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  workspace
    .command('delete [id-or-name]')
    .description('Delete a workspace (defaults to selected workspace)')
    .option('-y, --yes', 'Skip confirmation prompt (required in non-interactive shells)', false)
    .option('--dry-run', 'Preview which workspace would be deleted (no SDK call)')
    .action((idOrName: string | undefined, opts: { yes?: boolean; dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const config = getConfig()

        // Resolve the target. Accept ID or name; default to selected workspace.
        let targetId: string | undefined
        let targetName: string | undefined
        if (idOrName) {
          const data = await workspaces.listWorkspaces(arbi)
          const res = resolveWorkspaceSelector(data as WorkspaceRow[], idOrName)
          if (!res.ok) {
            printResolveError(data as WorkspaceRow[], idOrName, res)
            process.exit(1)
          }
          targetId = res.id
          targetName = res.ws.name
        } else {
          targetId = config?.selectedWorkspaceId
          if (!targetId) {
            error('No workspace ID given and no workspace selected. Run: arbi workspace select')
            process.exit(1)
          }
        }

        if (opts.dryRun) {
          const label = targetName ? `${targetId} ("${targetName}")` : targetId
          dryRun('delete workspace (along with all its documents, conversations, and tags)', label)
          return
        }

        // Confirmation. Destructive operation — protect against fat-finger.
        // In non-TTY (scripts/agents), require --yes explicitly so we never hang.
        const isInteractive = process.stdin.isTTY === true && process.stdout.isTTY === true
        if (!opts.yes) {
          if (!isInteractive) {
            error(
              `Refusing to delete ${targetId} without confirmation. Re-run with --yes (non-interactive shell).`
            )
            process.exit(1)
          }
          const label = targetName ? `"${targetName}" (${targetId})` : targetId
          const confirmed = await promptConfirm(
            `Delete workspace ${label}? This removes all documents, conversations, and tags in it.`,
            false
          )
          if (!confirmed) {
            console.log('Cancelled.')
            return
          }
        }

        await workspaces.deleteWorkspaces(arbi, [targetId])
        // If we deleted the currently selected workspace, clear selection + chat
        // session so subsequent commands don't operate on a stale ID.
        if (config?.selectedWorkspaceId === targetId) {
          updateConfig({ selectedWorkspaceId: undefined })
        }
        const session = getChatSession()
        if (session.workspaceId === targetId) {
          clearChatSession()
        }
        success(`Deleted workspace ${targetId}`)
      })()
    )

  // ── rename / describe / public ───────────────────────────────────────────
  // Named shortcuts over `workspace update` for the three properties agents
  // actually toggle. Same motivation as `doc share/unshare`: agents flub the
  // JSON-patch path more often than they flub a one-flag command.
  workspace
    .command('rename <name>')
    .description('Rename the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .action((name: string, opts: { workspace?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await workspaces.updateWorkspace(arbi, { name })
        success(`Renamed: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  workspace
    .command('describe <description>')
    .description("Update the workspace's description")
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .action((description: string, opts: { workspace?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = (await workspaces.updateWorkspace(arbi, {
          description,
        } as Parameters<typeof workspaces.updateWorkspace>[1])) as {
          external_id: string
          name: string
        }
        success(`Updated description: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  workspace
    .command('public <on-or-off>')
    .description('Toggle workspace visibility (on = public, off = private)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .action((onOff: string, opts: { workspace?: string }) =>
      runAction(async () => {
        const isPublic = onOff === 'on' || onOff === 'true' || onOff === 'public'
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = (await workspaces.updateWorkspace(arbi, {
          is_public: isPublic,
        } as Parameters<typeof workspaces.updateWorkspace>[1])) as {
          external_id: string
          name: string
        }
        success(
          `Visibility: ${isPublic ? 'public' : 'private'} — ${data.name} (${ref(data.external_id)})`
        )
      })()
    )

  workspace
    .command('update <json>')
    .description('Update workspace properties (pass JSON; prefer `rename`/`describe`/`public`)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .action((json: string, opts: { workspace?: string }) =>
      runAction(async () => {
        const body = parseJsonArg(json, 'arbi workspace update \'{"name": "New Name"}\'')
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await workspaces.updateWorkspace(arbi, body)
        success(`Updated: ${data.name} (${ref(data.external_id)})`)
      })()
    )

  workspace
    .command('users')
    .description('List users in the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('--json', 'Output as JSON')
    .action((opts: { workspace?: string; json?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await workspaces.listWorkspaceUsers(arbi)

        type User = {
          external_id?: string
          email?: string
          given_name?: string
          family_name?: string
        }

        if (opts.json) {
          const out = data.map((r) => {
            const u = (r.user as User) ?? {}
            return {
              user_id: u.external_id ?? null,
              email: u.email ?? null,
              name: [u.given_name, u.family_name].filter(Boolean).join(' ') || null,
              role: r.role,
              document_count: r.document_count,
              conversation_count: r.conversation_count,
            }
          })
          console.log(JSON.stringify(out, null, 2))
          return
        }

        if (data.length === 0) {
          console.log('No users found.')
          return
        }
        printTable(
          [
            {
              header: 'USER_ID',
              width: 16,
              value: (r) => (r.user as User)?.external_id ?? '',
            },
            {
              header: 'NAME',
              width: 20,
              value: (r) => {
                const u = r.user as User
                return [u?.given_name, u?.family_name].filter(Boolean).join(' ') || ''
              },
            },
            {
              header: 'EMAIL',
              width: 30,
              value: (r) => (r.user as User)?.email ?? '',
            },
            { header: 'ROLE', width: 14, value: (r) => r.role as string },
            { header: 'DOCS', width: 6, value: (r) => String(r.document_count) },
            { header: 'CONVS', width: 6, value: (r) => String(r.conversation_count) },
          ],
          data
        )
      })()
    )

  workspace
    .command('add-user <emails...>')
    .description('Add users to the active workspace')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('-r, --role <role>', 'Role: owner, collaborator, guest', 'collaborator')
    .action((emails: string[], opts: { workspace?: string; role?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const role = (opts.role ?? 'collaborator') as 'owner' | 'collaborator' | 'guest'
        const data = await workspaces.addWorkspaceUsers(arbi, emails, role)
        for (const u of data) success(`Added: ${u.user.email} as ${u.role}`)
      })()
    )

  workspace
    .command('request-access <owner>')
    .description('Ask another user to grant you access to one of their workspaces')
    .option('--note <note>', 'Optional message to the workspace owner')
    .option(
      '-w, --workspace <id>',
      'Target a specific workspace id (advanced — usually omitted; the owner picks)'
    )
    .action((owner: string, opts: { note?: string; workspace?: string }) =>
      runAction(async () => {
        const { arbi, crypto, loginResult } = await resolveDmCrypto()
        const resolved = await resolveRecipient(arbi, owner)
        if (!resolved || !resolved.pubKey) {
          error(
            `No contact, agent, or workspace member found for: ${owner}\n` +
              `Try: arbi contacts add ${owner.includes('@') ? owner : '<their-email>'}`
          )
          process.exit(3)
        }
        await dmOps.requestWorkspaceAccess(
          arbi,
          {
            recipientExtId: resolved.extId,
            recipientEncryptionPublicKey: resolved.pubKey,
            sessionPubkeyB64: arbi.crypto.bytesToBase64(loginResult.serverSessionKey),
            note: opts.note,
            workspaceExtId: opts.workspace,
          },
          crypto
        )
        success(
          `Requested workspace access from ${owner}` +
            (opts.workspace ? ` (workspace ${opts.workspace})` : '') +
            '. They’ll be notified and can grant temporary or permanent access.'
        )
      })()
    )

  workspace
    .command('remove-user <users...>')
    .description('Remove users from the active workspace (accepts usr-ids or emails)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('--dry-run', 'Preview which users would be removed (no SDK call)')
    .action((users: string[], opts: { workspace?: string; dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        // Accept emails too — humans have the email handy, not the usr- ID.
        // Look up the workspace member list and translate any email
        // arguments before passing through to the SDK.
        const needsLookup = users.some((u) => u.includes('@'))
        let userIds = users
        if (needsLookup) {
          const members = await workspaces.listWorkspaceUsers(arbi)
          userIds = users.map((u) => {
            if (!u.includes('@')) return u
            const hit = members.find((m) => (m as Record<string, unknown>).email === u) as
              Record<string, unknown> | undefined
            if (!hit) {
              error(`Workspace member ${u} not found. Try: arbi workspace users`)
              process.exit(3)
            }
            return hit.external_id as string
          })
        }
        if (opts.dryRun) {
          dryRun(`remove ${userIds.length} user(s) from this workspace`, userIds)
          return
        }
        await workspaces.removeWorkspaceUsers(arbi, userIds)
        success(`Removed ${userIds.length} user(s).`)
      })()
    )

  // ── leave ────────────────────────────────────────────────────────────────
  // Symmetric to `add-user`: lets an agent quit a workspace it no longer
  // needs without asking the owner to evict it. Backend's
  // /v1/workspace/users DELETE accepts self-removal — we just pass the
  // current user's id.
  workspace
    .command('leave [id-or-name]')
    .description('Leave a workspace (defaults to the active one)')
    .option('-y, --yes', 'Skip confirmation (required in non-TTY shells)')
    .option('--dry-run', 'Preview which workspace would be left (no SDK call)')
    .action((selector: string | undefined, opts: { yes?: boolean; dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi: userArbi } = await resolveAuth()
        const wsList = await workspaces.listWorkspaces(userArbi)
        const target = selector
          ? resolveWorkspaceSelector(wsList, selector)
          : (() => {
              const activeId = getConfig()?.selectedWorkspaceId
              const hit = wsList.find((w) => w.external_id === activeId)
              return hit
                ? ({ ok: true, id: hit.external_id, ws: hit } as const)
                : ({ ok: false, reason: 'not-found' } as const)
            })()
        if (!target.ok) {
          printResolveError(wsList, selector ?? '(active workspace)', target)
          process.exit(3)
        }

        const selfExtId = userArbi.session.getState().userExtId
        if (!selfExtId) {
          error('No user ID in session — cannot resolve self for leave.')
          process.exit(1)
        }

        if (opts.dryRun) {
          dryRun('leave workspace', `${target.id} ("${target.ws.name}")`)
          return
        }

        if (!opts?.yes) {
          requireInteractive('Pass -y/--yes to confirm in non-TTY shells.')
          const confirmed = await promptConfirm(
            `Leave workspace "${target.ws.name}" (${target.id})?`,
            false
          )
          if (!confirmed) {
            console.log('Cancelled.')
            return
          }
        }

        // Switch to the target workspace context so DELETE /v1/workspace/users
        // operates on the right scope, then self-remove by external_id.
        const { arbi } = await resolveWorkspace(target.id)
        await workspaces.removeWorkspaceUsers(arbi, [selfExtId])
        if (getConfig()?.selectedWorkspaceId === target.id) {
          updateConfig({ selectedWorkspaceId: '' })
        }
        success(`Left workspace "${target.ws.name}" (${target.id}).`)
      })()
    )

  workspace
    .command('set-role <role> <user-ids...>')
    .description('Update user roles (owner, collaborator, guest)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .action((role: string, userIds: string[], opts: { workspace?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace(opts.workspace)
        const data = await workspaces.setUserRole(
          arbi,
          userIds,
          role as 'owner' | 'collaborator' | 'guest'
        )
        for (const u of data) success(`Updated: ${u.user.email} → ${u.role}`)
      })()
    )

  workspace
    .command('copy <target-workspace> <doc-ids...>')
    .description('Copy documents to another workspace (target accepts ID or unique name)')
    .option('-w, --workspace <id>', 'Source workspace ID (defaults to selected workspace)')
    .action((targetRef: string, docIds: string[], opts: { workspace?: string }) =>
      runAction(async () => {
        // User-level auth to fetch workspace list (workspace-scoped JWTs cannot call /v1/user/workspaces)
        const { arbi: userArbi } = await resolveAuth()
        const wsList = await workspaces.listWorkspaces(userArbi)

        // Accept a workspace name as well as an ID — agents and humans both
        // tend to have the name handy. If multiple workspaces share the name,
        // refuse rather than silently picking one.
        let targetWs = wsList.find((w) => w.external_id === targetRef)
        if (!targetWs) {
          const nameMatches = wsList.filter(
            (w) => (w.name as string)?.toLowerCase() === targetRef.toLowerCase()
          )
          if (nameMatches.length === 1) {
            targetWs = nameMatches[0]
          } else if (nameMatches.length > 1) {
            error(
              `Ambiguous target workspace "${targetRef}" — matches ${nameMatches
                .map((w) => w.external_id)
                .join(', ')}. Use the ID.`
            )
            process.exit(3)
          }
        }
        if (!targetWs || !targetWs.wrapped_key) {
          error(`Target workspace ${targetRef} not found or has no encryption key`)
          process.exit(3)
        }
        const targetId = targetWs.external_id as string

        // Open source workspace to get workspace-scoped JWT + session key
        const { arbi, loginResult } = await resolveWorkspace(opts.workspace)
        const signingPrivateKeyBase64 = arbi.crypto.bytesToBase64(loginResult.signingPrivateKey)

        // Generate target key encrypted with session key
        const targetKey = await generateEncryptedWorkspaceKey(
          arbi,
          targetWs.wrapped_key,
          loginResult.serverSessionKey,
          signingPrivateKeyBase64
        )

        const data = await workspaces.copyDocuments(arbi, targetId, docIds, targetKey)
        const copied = data.documents_copied ?? 0
        const requested = docIds.length
        if (copied === 0) {
          error(`Copied 0 of ${requested} document(s). ${data.detail ?? ''}`.trim())
          error('Hint: documents must be fully indexed before they can be copied.')
          process.exit(1)
        }
        if (copied < requested) {
          error(`Copied ${copied} of ${requested} document(s). ${data.detail ?? ''}`.trim())
          return
        }
        success(`Copied ${copied} document(s) to ${targetId}`)
      })()
    )
}
